import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { safeHistoryReturn } from "@/lib/alertTrips";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { takeLogoutReason } from "@/lib/session";
import { MapPin, Radio, ShieldCheck, Activity, Mail, Lock, User, Loader2 } from "lucide-react";
import logo from "@/assets/boomerang_logo.png";

function formatError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

// Lu une seule fois par chargement de page (chaque déconnexion = reload complet).
// Évite que le double-montage StrictMode ne consomme le motif one-shot.
const INITIAL_EXPIRED_MSG = (() => {
  const r = takeLogoutReason();
  if (r === "inactivity") return "Votre session a expiré après une période d'inactivité.";
  if (r === "expired") return "Votre session a expiré. Veuillez vous reconnecter.";
  return "";
})();

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState("login");
  const [publicReg, setPublicReg] = useState(false);
  useEffect(() => { api.get("/auth/config").then((r) => setPublicReg(!!r.data.public_registration)).catch(() => setPublicReg(false)); }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [twofa, setTwofa] = useState(null); // { challenge_id, email, dev_code }
  const [code, setCode] = useState("");
  const [resetDevCode, setResetDevCode] = useState("");
  const [info, setInfo] = useState("");
  const [expiredMsg] = useState(INITIAL_EXPIRED_MSG);

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setInfo(""); setLoading(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setResetDevCode(res.data?.dev_code || "");
      setCode(""); setPassword("");
      setMode("reset");
      setInfo(`Si un compte existe pour ${email}, un code à 6 chiffres vient d'être envoyé par email.`);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/reset-password", { email, code: code.trim(), new_password: password });
      setMode("login"); setInfo(""); setResetDevCode(""); setCode(""); setPassword("");
      setError(""); 
      window.alert("Mot de passe réinitialisé. Vous pouvez maintenant vous connecter.");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login" ? { email, password } : { email, password, name };
      const res = await api.post(path, payload);
      if (res.data.twofa_required) {
        setTwofa({ challenge_id: res.data.challenge_id, email: res.data.email, dev_code: res.data.dev_code });
        setCode("");
        return;
      }
      setUser(res.data);
      await gotoHome();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const gotoHome = async () => {
    const returnTo = safeHistoryReturn(location.state?.returnTo);
    if (returnTo) { navigate(returnTo, { replace: true }); return; }
    let dest = "/dashboard";
    try { const r = await api.get("/me/settings"); if (r.data?.home_page) dest = r.data.home_page; } catch {}
    navigate(dest);
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/verify-2fa", { challenge_id: twofa.challenge_id, code: code.trim() });
      setUser(res.data);
      await gotoHome();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Left: form */}
      <div className="w-full lg:w-[46%] flex flex-col justify-center px-8 sm:px-16 relative">
        <div className="max-w-md w-full mx-auto">
          <img src={logo} alt="Boomerang Pacific Tracking" className="h-14 w-auto mb-10" data-testid="login-logo" />

          <p className="label-mono mb-3">Plateforme télématique</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] text-foreground">
            {twofa ? "Vérification" : mode === "forgot" ? "Mot de passe oublié" : mode === "reset" ? "Réinitialiser" : (mode === "login" ? "Connexion" : "Créer un compte")}
          </h1>
          <p className="mt-3 text-muted-foreground text-sm leading-relaxed">
            {twofa
              ? `Un code à 6 chiffres a été envoyé à ${twofa.email}. Saisissez-le pour continuer.`
              : mode === "forgot"
              ? "Saisissez votre email : nous vous enverrons un code pour définir un nouveau mot de passe."
              : mode === "reset"
              ? "Saisissez le code reçu par email et votre nouveau mot de passe."
              : "Gestion de flotte en temps réel pour vos traceurs Teltonika FMC130 et Pointer Cellocator CR300B."}
          </p>

          {expiredMsg && !twofa && (mode === "login") && (
            <div className="mt-6 rounded-md border border-[#F5821F]/40 bg-[#F5821F]/10 px-4 py-3 text-sm text-[#F5821F]" data-testid="session-expired-banner">
              {expiredMsg}
            </div>
          )}

          {twofa ? (
            <form onSubmit={verify2fa} className="mt-8 space-y-4" data-testid="twofa-form">
              {twofa.dev_code && (
                <p className="text-xs text-[#F5821F]" data-testid="twofa-dev-code">Mode démo — code : <b>{twofa.dev_code}</b></p>
              )}
              <Input icon={ShieldCheck} type="text" placeholder="Code à 6 chiffres" value={code} onChange={setCode} testid="twofa-code" required />
              {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="twofa-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-6 py-3.5 transition-colors">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Vérifier et se connecter"}
              </button>
              <button type="button" onClick={() => { setTwofa(null); setError(""); }} data-testid="twofa-back"
                className="w-full text-sm text-muted-foreground hover:text-foreground">Retour</button>
            </form>
          ) : mode === "forgot" ? (
            <form onSubmit={submitForgot} className="mt-8 space-y-4" data-testid="forgot-form">
              <Input icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="forgot-email" required />
              {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="forgot-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-6 py-3.5 transition-colors">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Envoyer le code"}
              </button>
              <button type="button" onClick={() => { setMode("login"); setError(""); setInfo(""); }} data-testid="forgot-back"
                className="w-full text-sm text-muted-foreground hover:text-foreground">Retour à la connexion</button>
            </form>
          ) : mode === "reset" ? (
            <form onSubmit={submitReset} className="mt-8 space-y-4" data-testid="reset-form">
              {info && <p className="text-xs text-[#17A2B8]" data-testid="reset-info">{info}</p>}
              {resetDevCode && <p className="text-xs text-[#F5821F]" data-testid="reset-dev-code">Mode démo — code : <b>{resetDevCode}</b></p>}
              <Input icon={ShieldCheck} type="text" placeholder="Code à 6 chiffres" value={code} onChange={setCode} testid="reset-code" required />
              <Input icon={Lock} type="password" placeholder="Nouveau mot de passe (min 6)" value={password} onChange={setPassword} testid="reset-password" required />
              {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
              <button type="submit" disabled={loading} data-testid="reset-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-6 py-3.5 transition-colors">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Réinitialiser le mot de passe"}
              </button>
              <button type="button" onClick={() => { setMode("forgot"); setError(""); }} data-testid="reset-back"
                className="w-full text-sm text-muted-foreground hover:text-foreground">Renvoyer un code</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            {mode === "register" && (
              <Input icon={User} type="text" placeholder="Nom complet" value={name} onChange={setName} testid="login-name" required />
            )}
            <Input icon={Mail} type="email" placeholder="Email" value={email} onChange={setEmail} testid="login-email" required />
            <Input icon={Lock} type="password" placeholder="Mot de passe" value={password} onChange={setPassword} testid="login-password" required />

            {mode === "login" && (
              <div className="text-right -mt-1">
                <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo(""); }} data-testid="forgot-link"
                  className="text-xs text-muted-foreground hover:text-[#17A2B8] transition-colors">Mot de passe oublié ?</button>
              </div>
            )}

            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}

            <button type="submit" disabled={loading} data-testid="login-submit"
              className="w-full flex items-center justify-center gap-2 bg-[#17A2B8] hover:bg-[#1391a5] disabled:opacity-60 text-white font-semibold rounded-md px-6 py-3.5 transition-colors">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (mode === "login" ? "Se connecter" : "Créer le compte")}
            </button>
          </form>
          )}

          {!twofa && publicReg && (mode === "login" || mode === "register") && (
          <p className="mt-5 text-sm text-muted-foreground">
            {mode === "login" ? "Pas encore de compte ?" : "Vous avez déjà un compte ?"}{" "}
            <button onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }} data-testid="toggle-mode"
              className="brand-teal font-semibold hover:underline">
              {mode === "login" ? "Créer un compte" : "Se connecter"}
            </button>
          </p>
          )}

          <div className="mt-10 grid grid-cols-2 gap-4">
            {[
              { icon: MapPin, t: "Cartographie live" },
              { icon: Radio, t: "Codec 8 + Cellocator" },
              { icon: Activity, t: "Alertes & rapports" },
              { icon: ShieldCheck, t: "Admin multi-compagnies" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                <f.icon className="w-4 h-4 brand-orange shrink-0" /> {f.t}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: image */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1760127831098-6a75c69ee79c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(9,9,11,0.95) 0%, rgba(9,9,11,0.4) 55%, rgba(23,162,184,0.25) 100%)" }} />
        <div className="absolute bottom-10 left-10 right-10 rounded-lg p-6 border border-white/10" style={{ background: "rgba(9,9,11,0.55)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)" }}>
          <p className="label-mono mb-1">Boomerang Pacific Tracking</p>
          <p className="text-lg text-white font-semibold">Solutions de suivi des véhicules et de gestion de flotte</p>
        </div>
      </div>
    </div>
  );
}

const Input = ({ icon: Icon, type, placeholder, value, onChange, testid, required }) => (
  <div className="relative">
    <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      data-testid={testid}
      className="w-full bg-secondary/60 border border-border rounded-md pl-11 pr-4 py-3 text-sm outline-none focus:border-[#17A2B8] text-foreground placeholder:text-muted-foreground"
    />
  </div>
);

